import React, { useEffect, useMemo, useState } from "react";
import Icons from "../icons";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/**
 * Hierarchical test-case picker used by the Execution flows.
 *
 * A board contains standalone test cases, sub-categories and groups; a
 * sub-category in turn contains test cases and groups. Groups are selected as a
 * whole (one checkbox toggles every member). A "Filter by ticket" dropdown
 * flattens the view to just the cases linked to a chosen Jira ticket.
 *
 * Selection state is owned by the parent:
 *   - isSelected(testcasekey) -> boolean
 *   - onToggleMany(caseObjects) -> toggle a set as a unit (single case = [tc],
 *     a group = its members). If all are already selected the parent clears
 *     them, otherwise it adds them.
 *   - disabledKey(testcasekey) -> boolean (already added elsewhere)
 */
export default function TestCasePicker({
  boards = [],
  isSelected,
  onToggleMany,
  disabledKey = () => false,
}) {
  const [boardId, setBoardId] = useState(boards[0]?.id || "");
  const [ticketFilter, setTicketFilter] = useState("");
  const [query, setQuery] = useState("");

  const [cases, setCases] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({}); // id -> bool

  // Load the board's cases, sub-categories and groups.
  useEffect(() => {
    if (!boardId) {
      setCases([]);
      setSubcats([]);
      setGroups([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setTicketFilter("");
    (async () => {
      try {
        const bid = encodeURIComponent(boardId);
        const [tcRes, subRes, grpRes] = await Promise.all([
          fetch(`${API_BASE_URL}testcases?board_id=${bid}`),
          fetch(`${API_BASE_URL}boards/${bid}/subcategories`),
          fetch(`${API_BASE_URL}boards/${bid}/groups`),
        ]);
        const tcBody = await tcRes.json().catch(() => ({}));
        const subBody = await subRes.json().catch(() => ({}));
        const grpBody = await grpRes.json().catch(() => ({}));
        if (cancelled) return;
        if (!tcRes.ok) throw new Error(tcBody?.detail || `HTTP ${tcRes.status}`);
        setCases(Array.isArray(tcBody?.data) ? tcBody.data : []);
        setSubcats(subRes.ok && Array.isArray(subBody?.data) ? subBody.data : []);
        setGroups(grpRes.ok && Array.isArray(grpBody?.data) ? grpBody.data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load test cases.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boardId]);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !isOpen(id) }));
  // Locations (board level + sub-categories) open by default; groups closed.
  const isOpen = (id) => (id in expanded ? expanded[id] : id.startsWith("loc:"));

  // Distinct Jira tickets linked to any case on this board — feeds the filter.
  const ticketOptions = useMemo(
    () =>
      Array.from(
        new Map(
          cases.flatMap((c) => c.linked_tickets || []).map((t) => [t.ticket_key, t])
        ).values()
      ).sort((a, b) => a.ticket_key.localeCompare(b.ticket_key)),
    [cases]
  );

  // Cases matching the ticket filter (flat), for the ticket view.
  const ticketCases = useMemo(() => {
    if (!ticketFilter) return [];
    const q = query.trim().toLowerCase();
    return cases.filter(
      (c) =>
        (c.linked_tickets || []).some((t) => t.ticket_key === ticketFilter) &&
        (!q || (c.name || "").toLowerCase().includes(q))
    );
  }, [cases, ticketFilter, query]);

  // Board level + each sub-category; within each: groups (whole) + ungrouped.
  const tree = useMemo(() => {
    const knownGroups = new Set(groups.map((g) => String(g.groupkey)));
    const subKeyOf = (tc) => (tc.subcategorykey ? String(tc.subcategorykey) : null);
    const groupKeyOf = (tc) =>
      tc.groupkey && knownGroups.has(String(tc.groupkey)) ? String(tc.groupkey) : null;
    const q = query.trim().toLowerCase();
    const matches = (tc) => !q || (tc.name || "").toLowerCase().includes(q);

    const locations = [
      { key: null, name: "Test Suite level", isBoard: true },
      ...subcats.map((s) => ({
        key: String(s.subcategorykey),
        name: s.name,
        isBoard: false,
      })),
    ];

    return locations
      .map((loc) => {
        const locTests = cases.filter((tc) => subKeyOf(tc) === loc.key);
        const groupsHere = groups.filter(
          (g) => (g.subcategorykey ? String(g.subcategorykey) : null) === loc.key
        );
        const groupNodes = groupsHere
          .map((g) => {
            const members = locTests.filter(
              (tc) => groupKeyOf(tc) === String(g.groupkey)
            );
            return {
              id: `grp:${g.groupkey}`,
              name: g.name,
              members,
              shown: members.filter(matches),
            };
          })
          .filter((n) => n.members.length > 0);
        const ungrouped = locTests.filter((tc) => groupKeyOf(tc) === null).filter(matches);
        return {
          id: `loc:${loc.key ?? "board"}`,
          name: loc.name,
          isBoard: loc.isBoard,
          groupNodes,
          ungrouped,
          count: locTests.length,
        };
      })
      .filter((loc) => loc.groupNodes.length > 0 || loc.ungrouped.length > 0);
  }, [cases, subcats, groups, query]);

  const selState = (caseObjs) => {
    if (!caseObjs.length) return "none";
    const sel = caseObjs.filter((c) => isSelected(c.testcasekey)).length;
    if (sel === 0) return "none";
    return sel === caseObjs.length ? "all" : "some";
  };

  const CaseRow = ({ tc, indent }) => {
    const disabled = disabledKey(tc.testcasekey);
    const checked = disabled || isSelected(tc.testcasekey);
    return (
      <label
        className={[
          "flex items-center gap-3 py-1.5 pr-3 text-sm",
          indent ? "pl-9" : "pl-3",
          disabled ? "opacity-50" : "cursor-pointer hover:bg-slate-50",
        ].join(" ")}
      >
        <input
          type="checkbox"
          disabled={disabled}
          checked={checked}
          onChange={() => onToggleMany([tc])}
          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        <span className="min-w-0 flex-1 truncate text-slate-800">{tc.name}</span>
        {(tc.linked_tickets || []).slice(0, 2).map((t) => (
          <span
            key={t.ticket_key}
            className="hidden flex-none rounded bg-brand-50 px-1.5 py-0.5 font-mono text-[0.625rem] font-medium text-brand-700 ring-1 ring-inset ring-brand-600/20 sm:inline"
          >
            {t.ticket_key}
          </span>
        ))}
        {disabled && <span className="flex-none text-xs text-slate-400">added</span>}
      </label>
    );
  };

  return (
    <div>
      {/* Board + ticket filter + search */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <span className="font-medium">Test Suite</span>
          <select
            value={boardId}
            onChange={(e) => setBoardId(e.target.value)}
            className="min-w-[13rem] rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
          >
            <option value="">Select a test suite…</option>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>

        {ticketOptions.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <span className="font-medium">Ticket</span>
            <select
              value={ticketFilter}
              onChange={(e) => setTicketFilter(e.target.value)}
              className="min-w-[12rem] rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
            >
              <option value="">All tickets</option>
              {ticketOptions.map((t) => (
                <option key={t.ticket_key} value={t.ticket_key}>
                  {t.ticket_key}
                  {t.summary ? ` — ${t.summary}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="relative flex-1 sm:max-w-xs">
          <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search test cases…"
            className="w-full rounded-lg border border-slate-300 bg-card py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25"
          />
        </div>
      </div>

      {!boardId ? (
        <div className="rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center text-sm text-slate-500">
          Pick a test suite to see its test cases.
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-400">
          <Icons.Spinner className="h-5 w-5 animate-spin" />
          Loading test cases…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : ticketFilter ? (
        // Flat ticket-filtered view.
        ticketCases.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center text-sm text-slate-500">
            No test cases linked to <span className="font-mono">{ticketFilter}</span> on this board.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 divide-y divide-slate-100">
            {ticketCases.map((tc) => (
              <CaseRow key={tc.testcasekey} tc={tc} />
            ))}
          </div>
        )
      ) : tree.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center text-sm text-slate-500">
          No test cases in this test suite.
        </div>
      ) : (
        <div className="space-y-3">
          {tree.map((loc) => (
            <div key={loc.id} className="overflow-hidden rounded-xl border border-slate-200">
              {/* Location header */}
              <button
                type="button"
                onClick={() => toggleExpand(loc.id)}
                className="flex w-full items-center gap-2 bg-slate-50 px-3 py-2 text-left"
              >
                <Icons.ChevronRight
                  className={[
                    "h-4 w-4 flex-none text-slate-400 transition-transform",
                    isOpen(loc.id) ? "rotate-90" : "",
                  ].join(" ")}
                />
                {loc.isBoard ? (
                  <Icons.Boards className="h-4 w-4 flex-none text-slate-500" />
                ) : (
                  <Icons.Grid className="h-4 w-4 flex-none text-brand-500" />
                )}
                <span className="flex-1 truncate font-semibold text-slate-800">{loc.name}</span>
                <span className="flex-none rounded-full bg-slate-200 px-2 py-0.5 text-[0.6875rem] font-medium text-slate-600">
                  {loc.count}
                </span>
              </button>

              {isOpen(loc.id) && (
                <div className="divide-y divide-slate-100">
                  {/* Groups — selected as a whole */}
                  {loc.groupNodes.map((g) => {
                    const state = selState(g.members);
                    const gOpen = isOpen(g.id);
                    return (
                      <div key={g.id}>
                        <div className="flex items-center gap-3 py-1.5 pl-3 pr-3 text-sm">
                          <input
                            type="checkbox"
                            checked={state === "all"}
                            ref={(el) => {
                              if (el) el.indeterminate = state === "some";
                            }}
                            onChange={() => onToggleMany(g.members)}
                            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                          />
                          <button
                            type="button"
                            onClick={() => toggleExpand(g.id)}
                            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                          >
                            <Icons.ChevronRight
                              className={[
                                "h-3.5 w-3.5 flex-none text-slate-400 transition-transform",
                                gOpen ? "rotate-90" : "",
                              ].join(" ")}
                            />
                            <Icons.Boards className="h-3.5 w-3.5 flex-none text-brand-500" />
                            <span className="truncate font-medium text-slate-800">{g.name}</span>
                            <span className="flex-none rounded-full bg-brand-50 px-1.5 py-0.5 text-[0.625rem] font-medium text-brand-700">
                              group · {g.members.length}
                            </span>
                          </button>
                        </div>
                        {/* Read-only preview of group members. */}
                        {gOpen && (
                          <ul className="mb-1 ml-9 border-l border-slate-200 pl-3 text-xs text-slate-500">
                            {g.members.map((m) => (
                              <li key={m.testcasekey} className="truncate py-0.5">
                                {m.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}

                  {/* Ungrouped standalone cases — individually selectable */}
                  {loc.ungrouped.map((tc) => (
                    <CaseRow key={tc.testcasekey} tc={tc} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
